import { createEffect, createRoot } from 'solid-js';
import { LoadState } from '@trimble/data-loading';
import { getLogger } from '@trimble/logging';
import { ProjectDataState, TrajectoryItem } from './DataStateModels';

/**
 * Resolves a trajectory's `isSplittable` flag once it settles, so the loader
 * can pick between section-tagged and single-tag TrimBIM export.
 *
 * Resolves with the item when splittability is known; resolves with
 * `undefined` when the trajectory is gone or the signal aborts. If an item is
 * marked splittable but has no mission (an inconsistent state that would
 * otherwise never settle), it resolves with the item and logs a warning rather
 * than hanging.
 */
export function resolveTrajectorySplittable(
  projectDataState: ProjectDataState,
  id: string,
  signal?: AbortSignal
): Promise<TrajectoryItem | undefined> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve(undefined);
      return;
    }
    createRoot((dispose) => {
      let onAbort: (() => void) | undefined;
      const finish = (item: TrajectoryItem | undefined): void => {
        if (onAbort) signal?.removeEventListener('abort', onAbort);
        dispose();
        resolve(item);
      };
      if (signal) {
        onAbort = (): void => finish(undefined);
        signal.addEventListener('abort', onAbort, { once: true });
      }
      createEffect(() => {
        if (projectDataState.trajectory.state !== LoadState.LoadComplete) {
          return;
        }
        const item = projectDataState.trajectory.items[id];
        if (!item) {
          finish(undefined);
          return;
        }
        if (item.isSplittable === undefined) return;
        if (item.isSplittable === true && !item.mission) {
          getLogger().warn(
            `[resolveTrajectorySplittable] ${id} is splittable but has no mission; loading without sections`
          );
          finish(item);
          return;
        }
        finish(item);
      });
    });
  });
}
